import type { LucideIcon } from "lucide-react";
import {
  Banknote,
  Building,
  Building2,
  CalendarDays,
  CalendarRange,
  CarFront,
  LayoutDashboard,
  Car,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Coins,
  Database,
  FileCheck2,
  Fingerprint,
  FolderTree,
  HandCoins,
  IdCard,
  KeyRound,
  Layers,
  ListTree,
  PackageOpen,
  Receipt,
  ScrollText,
  Server,
  Shield,
  SlidersHorizontal,
  Tag,
  Tags,
  MapPin,
  ShoppingCart,
  Timer,
  TrendingUp,
  Truck,
  Users,
  Wallet,
  Table,
} from "lucide-react";

export type AppNavLeafItem = { title: string; url: string; icon: LucideIcon };

export const dashboardNavItem: AppNavLeafItem = { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard };

export const productNavItem: AppNavLeafItem = { title: "Products (Cars)", url: "/products", icon: Car };
export const salesNavItem: AppNavLeafItem = { title: "Sales", url: "/product-sales", icon: Receipt };

export const catalogNavItems: AppNavLeafItem[] = [
  { title: "Categories", url: "/categories", icon: FolderTree },
  { title: "Brands", url: "/brands", icon: Tag },
  { title: "Model", url: "/vehicle-models", icon: CarFront },
  { title: "Version", url: "/vehicle-versions", icon: PackageOpen },
  { title: "Purchase cost types", url: "/purchase-cost-types", icon: Receipt },
  { title: "Attribute groups", url: "/attribute-groups", icon: Layers },
  { title: "Attributes", url: "/attributes", icon: ListTree },
  { title: "Location", url: "/ware-houses", icon: MapPin },
];

export const customerSupplierNavItems: AppNavLeafItem[] = [
  { title: "Customers", url: "/customers", icon: Users },
  { title: "Suppliers", url: "/suppliers", icon: Truck },
];

export const employeeOnlyNavItems: AppNavLeafItem[] = [
  { title: "Employees", url: "/employees", icon: Users },
  { title: "Departments", url: "/departments", icon: Building2 },
  { title: "Designations", url: "/designations", icon: IdCard },
];

export const leaveNavItems: AppNavLeafItem[] = [
  { title: "Leave period", url: "/leave-years", icon: CalendarRange },
  { title: "Leave types", url: "/leave-types", icon: CalendarDays },
  { title: "Leave policies", url: "/leave-policies", icon: ScrollText },
];

export const attendanceNavItems: AppNavLeafItem[] = [
  { title: "Work shifts", url: "/work-shifts", icon: Clock },
  { title: "Rosters", url: "/rosters", icon: Table },
  { title: "Working calendar", url: "/working-calendar", icon: CalendarDays },
  { title: "Daily attendance", url: "/attendance/daily", icon: ClipboardCheck },
  { title: "Punch log", url: "/attendance/punches", icon: Fingerprint },
  { title: "Attendance rules", url: "/attendance/rules", icon: SlidersHorizontal },
];

export const expenseNavItems: AppNavLeafItem[] = [
  { title: "Expense categories", url: "/expense-categories", icon: Tags },
  { title: "Expenses", url: "/expenses", icon: Receipt },
];

export const dataBankNavItems: AppNavLeafItem[] = [
  { title: "Banks", url: "/banks", icon: Building2 },
];

/** Permission categories, Permissions, Roles — not companies. */
export const accessPermissionsNavItems: AppNavLeafItem[] = [
  { title: "Permission categories", url: "/permission-categories", icon: Layers },
  { title: "Permissions", url: "/permissions", icon: KeyRound },
  { title: "Roles", url: "/roles", icon: Shield },
];

export const companyNavItems: AppNavLeafItem[] = [
  { title: "Companies", url: "/companies", icon: Building },
  { title: "Biometric devices", url: "/biometric-devices", icon: Fingerprint },
  { title: "Company devices", url: "/company-biometric-devices", icon: Server },
];

export const workPoliciesNavItem: AppNavLeafItem = {
  title: "Work policies",
  url: "/work-policies",
  icon: FileCheck2,
};

export const leaveRequestsNavItem: AppNavLeafItem = {
  title: "Leave requests",
  url: "/leave-requests",
  icon: ClipboardList,
};

export const payrollNavItems: AppNavLeafItem[] = [
  { title: "Salary policies", url: "/salary-policies", icon: Banknote },
  { title: "Allowance Types", url: "/employee-allowance-types", icon: Coins },
  { title: "Deduction Types", url: "/employee-deduction-types", icon: Coins },
  { title: "Attendances", url: "/payroll/attendances", icon: ClipboardCheck },
  { title: "Salaries", url: "/payroll/salary-batches", icon: Layers },
  { title: "Salary increments", url: "/payroll/salary-increments", icon: TrendingUp },
  { title: "Employee loans", url: "/payroll/loans", icon: Receipt },
  { title: "Advance salary", url: "/payroll/salary-advances", icon: HandCoins },
  { title: "Salary payments", url: "/payroll/salary-payments", icon: Wallet },
];

/** Top-level app modules (sidebar: one collapsible group each with nested leaf links). */
export type AppNavModuleMeta = {
  id: string;
  title: string;
  icon: LucideIcon;
};

export const APP_NAV_MODULE_META: AppNavModuleMeta[] = [
  { id: "products-inventory", title: "Products & Inventory", icon: Layers },
  { id: "sales", title: "Sales", icon: ShoppingCart },
  { id: "customer-supplier", title: "Customer & Supplier", icon: Users },
  { id: "employee", title: "Employees", icon: IdCard },
  { id: "leave", title: "Leave", icon: CalendarRange },
  { id: "payroll", title: "Payroll", icon: Banknote },
  { id: "attendance", title: "Attendance", icon: Timer },
  { id: "expense", title: "Expenses", icon: Receipt },
  { id: "data-bank", title: "Data Bank", icon: Database },
  { id: "access", title: "Access & Permissions", icon: Shield },
  { id: "company", title: "Companies", icon: Building },
];

export function pathMatchesNavLeaf(pathname: string, url: string): boolean {
  return pathname === url || pathname.startsWith(url + "/");
}
